/**
 * DocumentHashComputer - Utility for computing document hash for cache invalidation
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility computes a hash of document text and font settings to detect
 * when the document has changed and cache should be invalidated.
 */

/**
 * Compute document hash for change detection
 * 
 * Includes font information in hash to invalidate cache on font changes.
 * This ensures that changing from "Roboto Regular" to "Roboto Bold" invalidates pagination.
 * 
 * @param text - Document text
 * @param fontFamily - Font family name (optional, defaults to 'Inter' for consistency)
 * @param fontWeight - Font weight (optional, defaults to 400 for consistency)
 * @param fontStyle - Font style (optional, defaults to 'normal' for consistency)
 * @param fontSizePx - Font size in pixels (optional, for cache invalidation)
 * @returns Hash string (base36 encoded, always positive)
 */
export function computeDocHash(
  text: string,
  fontFamily?: string,
  fontWeight?: number,
  fontStyle?: string,
  fontSizePx?: number
): string {
  let hash = 0;
  
  // ✅ FIX: Use consistent defaults to prevent hash inconsistencies
  const normalizedFontFamily = fontFamily || 'Inter';
  const normalizedFontWeight = fontWeight ?? 400;
  const normalizedFontStyle = fontStyle || 'normal';
  
  // Hash the text content
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) - hash) + text.charCodeAt(i);
    hash = hash | 0; // ✅ FIX: Convert to signed 32-bit integer (was: hash & hash which does nothing)
  }
  
  // ✅ Include separator to prevent collisions between text and font components
  // Hash a separator character to distinguish text from font
  hash = ((hash << 5) - hash) + 0x1F; // Use DEL character as separator
  hash = hash | 0;
  
  // ✅ Include font information in hash to invalidate cache on font changes
  // This ensures that changing from "Roboto Regular" to "Roboto Bold" invalidates pagination
  for (let i = 0; i < normalizedFontFamily.length; i++) {
    hash = ((hash << 5) - hash) + normalizedFontFamily.charCodeAt(i);
    hash = hash | 0;
  }
  
  hash = ((hash << 5) - hash) + normalizedFontWeight;
  hash = hash | 0;
  
  for (let i = 0; i < normalizedFontStyle.length; i++) {
    hash = ((hash << 5) - hash) + normalizedFontStyle.charCodeAt(i);
    hash = hash | 0;
  }
  
  // ✅ Include font size if provided
  if (fontSizePx !== undefined) {
    hash = ((hash << 5) - hash) + fontSizePx;
    hash = hash | 0;
  }
  
  // ✅ FIX: Ensure hash is always positive for consistent cache keys
  // Convert to unsigned 32-bit integer, then to base36
  const unsignedHash = hash >>> 0;
  return unsignedHash.toString(36);
}

